/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/prop-types */
import React from 'react';

import { Grid, Header, Card } from 'semantic-ui-react';

import { graphql } from 'gatsby';
import _isEmpty from 'lodash/isEmpty';

import {
  BlogPostSchema,
  SEO,
  BlogPostBreadcrumbs,
  Section,
  FadeReveal,
  Image,
  MarkdownContent,
  FormattedDate,
  BlogPostLink,
  SlideReveal,
} from '@bluefin/components';

import { createSchemaProps, formatAuthor, getRelatedArticles } from '../../template-utils';

export default function Template({ data, pageContext }) {
  const {
    fishermanBusiness: { name: businessName, logo: businessLogo, businessCategories = [] },
    fishermanBusinessBlogPost: blogPost,
    allFishermanBusinessBlogPost: { nodes: allBlogs = [] },
  } = data;

  const {
    _id: blogId,
    destination,
    title,
    publishedAt,
    heroImage,
    gatsbyHeroImage,
    author,
    content,
    summary,
    seoDescription,
    seoTitle,
    heroImageAltText,
  } = blogPost || {};

  const { blogRootSlug, templateProps = {} } = pageContext;

  const { relatedArticlesText = 'Related Articles' } = templateProps;

  const schemaProps = createSchemaProps(blogPost, businessName, businessCategories, businessLogo);

  const relatedArticles = getRelatedArticles(allBlogs, blogId, destination);

  return (
    <>
      <SEO
        businessName={businessName}
        pageName={title}
        title={seoTitle}
        description={seoDescription || summary}
        truncateDescription
      />
      <BlogPostSchema {...schemaProps} />

      <div className="blog-page-container">
        <BlogPostBreadcrumbs
          title={title}
          blogRootSlug={blogRootSlug}
          dividerContent="/"
          linkIcon="fancy-arrow-left"
        />

        <Section className="main-section">
          <Grid stackable doubling>
            <Grid.Column className="blog-content" width={11}>
              <FadeReveal triggerOnce>
                <Image
                  className="featured overlay"
                  src={gatsbyHeroImage || heroImage}
                  background
                  altText={heroImageAltText}
                  loadStrategy="eager"
                >
                  <Header as="h1" className="title">
                    {title}
                  </Header>
                  {!_isEmpty(publishedAt) && (
                    <FormattedDate date={publishedAt} outputFormat="MMM. d, yyyy" />
                  )}
                  {!_isEmpty(author) && <span className="author">{formatAuthor(author)}</span>}
                </Image>
              </FadeReveal>
              <article>
                <MarkdownContent content={content} />
              </article>
            </Grid.Column>

            {!_isEmpty(relatedArticles) && (
              <Grid.Column className="related-blogs" width={5}>
                <Header as="h2">{relatedArticlesText}</Header>
                <Card.Group itemsPerRow={1}>
                  <SlideReveal className="ui card" triggerOnce direction="up" fade>
                    {relatedArticles.map(
                      ({
                        _id: relatedArticleId,
                        title: relatedArticleTitle,
                        slug: relatedArticleSlug,
                        publishedAt: relatedArticlePublishedAt,
                      }) => (
                        <BlogPostLink
                          key={relatedArticleId}
                          title={relatedArticleTitle}
                          slug={relatedArticleSlug}
                        >
                          <Card className="related-article" link>
                            <Card.Content>
                              <div className="header-container">
                                <span className="title">{relatedArticleTitle}</span>
                                <FormattedDate
                                  date={relatedArticlePublishedAt}
                                  outputFormat="MMM. d"
                                />
                              </div>
                            </Card.Content>
                          </Card>
                        </BlogPostLink>
                      ),
                    )}
                  </SlideReveal>
                </Card.Group>
              </Grid.Column>
            )}
          </Grid>
        </Section>
      </div>
    </>
  );
}

export const query = graphql`
  query ($id: Int!) {
    fishermanBusiness {
      name
      logo
      businessCategories {
        category
      }
    }
    fishermanBusinessBlogPost(_id: { eq: $id }) {
      _id
      summary
      title
      content
      heroImage
      gatsbyHeroImage {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      author {
        firstName
        lastName
        avatar
        email
      }
      publishedAt
      seoTitle
      seoDescription
      heroImageAltText
      destination
    }
    allFishermanBusinessBlogPost {
      nodes {
        _id
        title
        destination
        publishedAt
        slug
      }
    }
  }
`;
